import React from "react";

import { useLiveUpdates } from "@hooks/useLiveUpdates";
import { IdTooltip } from "@src/components/elements/IdTooltip";
import { buildThemeClasses } from "@src/utils/theme";
import { getContentfulPreviewPropsFn } from "@utils";
import clsx from "clsx";
import { graphql } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

export function SectionTrustbar({ entry: rawEntry }) {
  const entry = useLiveUpdates(rawEntry);
  const getPreviewProps = getContentfulPreviewPropsFn(entry.contentful_id);
  const logos =
    (entry.logosCollection?.items
      ? entry.logosCollection.items
      : entry.logos) ?? [];

  return (
    <section
      className={clsx(
        "font-atkinson",
        { ...buildThemeClasses(entry.customTheme)}
      )}
      id={entry.contentful_id}
    >
      <IdTooltip className="mt-3" id={entry.contentful_id} />
      <div className="inner flex flex-col md:flex-row md:justify-between items-center gap-8 md:gap-16 py-section">
        <h4
          className="text-rt-2m max-w-350px shrink-0 text-center md:text-left"
          {...getPreviewProps("headline")}
        >
          {entry.headline}
        </h4>

        <div
          className="flex gap-12 md:gap-14 flex-wrap justify-around items-center md:justify-start"
          {...getPreviewProps("logos")}
        >
          {logos?.map((logo, index) => (
            <React.Fragment key={`trustbar-${logo?.description}-${index}`}>
              {logo?.gatsbyImageData && (
                <GatsbyImage
                  alt={logo?.description}
                  className={clsx("h-auto w-auto max-w-[130px] max-h-[48px]")}
                  image={getImage(logo?.gatsbyImageData)}
                />
              )}
              {!logo?.gatsbyImageData && (
                <img
                  alt={logo?.description}
                  aria-hidden="true"
                  className={clsx("h-auto w-auto max-w-[130px] max-h-[48px]")}
                  height="48"
                  src={logo?.url ?? `https:${logo?.file?.url}`}
                  width="130"
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

export const SectionTrustbarFragment = graphql`
  fragment SectionTrustbar on ContentfulSectionTrustbar {
    contentful_id
    __typename
    headline
    customTheme
    logos {
      contentful_id
      __typename
      title
      description
      file {
        url
        fileName
        contentType
      }
      gatsbyImageData(
        resizingBehavior: PAD
        placeholder: BLURRED
        width: 130
        height: 48
        quality: 80
      )
    }
  }
`;
